/**
 * Google Drive integration API client.
 *
 * Backend endpoints under /api/integrations/google-drive/:
 *   GET  auth-url/?platform=web|mobile  -> returns Google authorize URL
 *   GET  callback/                       -> handled directly by backend (302 redirect)
 *   GET  status/                         -> connection state + quota
 *   POST disconnect/                     -> revoke + mark inactive
 *   POST test/                           -> health check
 *
 * Auth, JWT, and workspace switching (owner_account_id) are handled by the
 * shared apiService interceptors in src/services/api.js.
 */

import { apiService } from '../api'

export const googleDriveAPI = {
  /**
   * Get the Google authorize URL for connecting Drive to the active workspace.
   * @param {'web'|'mobile'} platform
   */
  getAuthUrl: (platform = 'web') =>
    apiService.get('/integrations/google-drive/auth-url/', {
      params: { platform },
    }),

  /** Get the current Drive connection status for the active workspace. */
  getStatus: () => apiService.get('/integrations/google-drive/status/'),

  /** Disconnect Drive for the active workspace. Files in Drive are kept. */
  disconnect: () => apiService.post('/integrations/google-drive/disconnect/'),

  /** Run a health check against the connected Drive (token refresh + about.get). */
  test: () => apiService.post('/integrations/google-drive/test/'),

  /**
   * Config for the client-side Google Picker (access_token, api_key, app_id,
   * client_id). Owner-only. 503 if GOOGLE_PICKER_API_KEY isn't set server-side.
   */
  pickerConfig: () => apiService.get('/integrations/google-drive/picker-config/'),

  /**
   * Import user-picked Drive files into OVACS (copies them into the OVACS
   * folder and registers each as a Document).
   * @param {{file_ids: string[], security_level?: string, case_id?, session_id?, client_id?, group_id?}} payload
   */
  importFiles: (payload) =>
    apiService.post('/integrations/google-drive/import/', payload),

  /**
   * Re-link files created under a PREVIOUS OVACS Google connection (e.g. after
   * the Google Cloud project behind OVACS was recreated). candidates() probes
   * which Drive-backed documents the current app can no longer open; apply()
   * confirms the per-file grant the owner just gave through the Picker and
   * clears the documents' missing flag. Nothing is copied or moved.
   */
  relink: {
    candidates: () => apiService.get('/integrations/google-drive/relink/candidates/'),
    apply: (fileIds) =>
      apiService.post('/integrations/google-drive/relink/', { file_ids: fileIds }),
  },

  /**
   * Forward migration — move existing local files into the connected Drive.
   * One job per workspace at a time. Owner-only.
   */
  migration: {
    start: () => apiService.post('/integrations/google-drive/migration/start/'),
    status: () => apiService.get('/integrations/google-drive/migration/status/'),
    cancel: () => apiService.post('/integrations/google-drive/migration/cancel/'),
  },
}

export default googleDriveAPI
